import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";

const optionButtonStyle = (active) => ({
    border: "1px solid",
    borderColor: active ? "#284eb8" : "#d7dff4",
    background: active ? "#eef3ff" : "#ffffff",
    color: active ? "#1f3f9d" : "#5f6b8a",
    borderRadius: "999px",
    padding: "10px 14px",
    fontSize: "0.95rem",
    fontWeight: 600,
    lineHeight: 1.25,
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    minHeight: "48px",
});

const bulletStyle = (active) => ({
    width: "14px",
    height: "14px",
    borderRadius: "50%",
    border: `2px solid ${active ? "#284eb8" : "#c8d2ee"}`,
    background: active ? "#284eb8" : "#ffffff",
    boxShadow: active ? "0 0 0 4px rgba(40, 78, 184, 0.12)" : "none",
    flexShrink: 0,
});

const parseDuration = (value) => {
    const numeric = Number.parseInt(value, 10);
    return Number.isNaN(numeric) ? 999 : numeric;
};

const normalizeAgeGroup = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw) return "";
    if (raw === "18_24" || raw === "18-24" || raw === "18-24 years") return "18_24";
    if (raw === "25_34" || raw === "25-34" || raw === "25-34 years") return "25_34";
    if (raw === "35_plus" || raw === "35+" || raw === "35+ years") return "35_plus";
    return raw;
};

const scoreCourse = (course, answers) => {
    const content = `${course.title || ""} ${course.description || ""}`.toLowerCase();
    let score = 0;

    const hasAny = (keywords) => keywords.some((keyword) => content.includes(keyword));
    const duration = parseDuration(course.duration);
    const ageGroups = Array.isArray(course.recommendation_age_groups)
        ? course.recommendation_age_groups.map(normalizeAgeGroup).filter(Boolean)
        : [];

    if (ageGroups.length > 0) {
        score += ageGroups.includes(normalizeAgeGroup(answers.ageGroup)) ? 8 : -4;
    }
    if (answers.carType === "manual" && hasAny(["manual", "gear", "clutch"])) score += 8;
    if (answers.carType === "automatic" && hasAny(["automatic", "auto"])) score += 8;

    if (answers.preferredTime === "morning" && hasAny(["morning", "am", "early"])) score += 4;
    if (answers.preferredTime === "afternoon" && hasAny(["afternoon", "day"])) score += 4;
    if (answers.preferredTime === "evening" && hasAny(["evening", "pm"])) score += 4;
    if (answers.preferredTime === "weekend" && hasAny(["weekend", "saturday", "sunday"])) score += 5;
    if (answers.preferredTime === "flexible") score += 2;

    if (answers.driverPreference === "female" && hasAny(["female instructor", "lady instructor", "women instructor"])) {
        score += 7;
    }
    if (answers.driverPreference === "male" && hasAny(["male instructor"])) {
        score += 5;
    }

    if (answers.learnerGender === "female" && hasAny(["women", "female", "lady"])) {
        score += 3;
    }

    if (answers.ageGroup === "18_24" && duration >= 10 && duration <= 30) score += 1;
    if (answers.ageGroup === "25_34" && duration >= 8 && duration <= 25) score += 1;
    if (answers.ageGroup === "35_plus" && duration <= 18) score += 2;

    if (score <= 0) {
        score = Math.max(1, 30 - duration);
    }

    return score;
};

const CourseRecommendationModal = ({
    show,
    onClose,
    courses = [],
    onSelectCourse,
    onViewCourse,
    onApplyPreferences,
}) => {
    const [ageGroup, setAgeGroup] = useState("");
    const [carType, setCarType] = useState("");
    const [learnerGender, setLearnerGender] = useState("");
    const [preferredTime, setPreferredTime] = useState("");
    const [driverPreference, setDriverPreference] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showResults, setShowResults] = useState(false);

    const allAnswered = Boolean(ageGroup && carType && learnerGender && preferredTime && driverPreference);

    const recommendedCourses = !allAnswered || !Array.isArray(courses) || courses.length === 0
        ? []
        : [...courses]
            .map((course) => ({
                ...course,
                recommendationScore: scoreCourse(course, {
                    ageGroup,
                    carType,
                    learnerGender,
                    preferredTime,
                    driverPreference,
                }),
                normalizedDuration: parseDuration(course.duration),
            }))
            .sort((first, second) => {
                if (second.recommendationScore !== first.recommendationScore) {
                    return second.recommendationScore - first.recommendationScore;
                }
                return first.normalizedDuration - second.normalizedDuration;
            })
            .slice(0, 3);

    useEffect(() => {
        if (show) {
            setIsSubmitting(false);
            setShowResults(false);
        }
    }, [show]);

    if (!show) {
        return null;
    }

    const handleConfirm = async () => {
        if (!allAnswered || isSubmitting) {
            return;
        }

        const answers = { ageGroup, carType, learnerGender, preferredTime, driverPreference };
        setIsSubmitting(true);

        await new Promise((resolve) => {
            window.setTimeout(resolve, 850);
        });

        if (typeof onApplyPreferences === "function") {
            await Promise.resolve(onApplyPreferences(answers));
            setIsSubmitting(false);
            onClose?.();
            return;
        }
        setIsSubmitting(false);
        setShowResults(true);
    };

    const renderOption = (label, value, selectedValue, onSelect) => (
        <button
            key={value}
            type="button"
            style={optionButtonStyle(selectedValue === value)}
            onClick={() => onSelect(value)}
        >
            <span style={bulletStyle(selectedValue === value)} />
            <span>{label}</span>
        </button>
    );

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 13000,
                background: "rgba(9, 20, 48, 0.72)",
                backdropFilter: "blur(5px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "12px",
            }}
        >
            <div
                style={{
                    width: "min(960px, 100%)",
                    maxHeight: "min(94vh, 860px)",
                    overflowY: "auto",
                    borderRadius: "22px",
                    background: "#ffffff",
                    boxShadow: "0 30px 70px rgba(11, 27, 63, 0.26)",
                    position: "relative",
                    padding: "18px",
                }}
            >
                {isSubmitting ? (
                    <div
                        style={{
                            minHeight: "520px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            gap: "22px",
                        }}
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close recommendation popup"
                            style={{
                                position: "absolute",
                                top: "14px",
                                right: "14px",
                                width: "40px",
                                height: "40px",
                                border: "0",
                                borderRadius: "14px",
                                background: "#284eb8",
                                color: "#ffffff",
                                fontSize: "1.5rem",
                                lineHeight: 1,
                                cursor: "pointer",
                            }}
                        >
                            x
                        </button>
                        <div
                            className="course-recommendation-loader"
                            style={{
                                width: "120px",
                                height: "120px",
                                borderRadius: "50%",
                                border: "5px solid #cfd9ff",
                                borderTopColor: "#284eb8",
                            }}
                        />
                        <h3
                            style={{
                                margin: 0,
                                color: "#1a2d83",
                                fontSize: "2rem",
                                lineHeight: 1.15,
                                fontWeight: 800,
                            }}
                        >
                            Recommending the best course for you
                        </h3>
                    </div>
                ) : null}

                {!isSubmitting ? (
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close recommendation popup"
                    style={{
                        position: "absolute",
                        top: "14px",
                        right: "14px",
                        width: "40px",
                        height: "40px",
                        border: "0",
                        borderRadius: "14px",
                        background: "#284eb8",
                        color: "#ffffff",
                        fontSize: "1.5rem",
                        lineHeight: 1,
                        cursor: "pointer",
                    }}
                >
                    x
                </button>
                ) : null}

                {!isSubmitting ? (
                <div>
                    <div
                        style={{
                            borderRadius: "24px",
                            padding: "18px",
                            background: "#ffffff",
                            border: "1px solid #e6ebf7",
                        }}
                    >
                        <div
                            style={{
                                width: "100%",
                                marginBottom: "12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                                <span
                                    aria-hidden="true"
                                    style={{
                                        width: "60px",
                                        height: "60px",
                                        borderRadius: "50%",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: "transparent",
                                        color: "#111111",
                                    }}
                                >
                                    <i className="bi bi-emoji-smile" style={{ fontSize: "2rem", lineHeight: 1 }}></i>
                                </span>
                        </div>
                        <h3
                            style={{
                                fontSize: "1.7rem",
                                lineHeight: 1.15,
                                marginBottom: "8px",
                                color: "#132347",
                                fontWeight: 800,
                            }}
                        >
                            Help Us Recommend the Right Course
                        </h3>
                        <p
                            style={{
                                marginBottom: "16px",
                                color: "#5a6888",
                                fontSize: "0.95rem",
                                lineHeight: 1.5,
                            }}
                        >
                            Answer five quick questions and we will highlight the courses
                            that best match your age, car type, preferred timing, and instructor preference.
                        </p>

                        <div style={{ display: "grid", gap: "16px" }}>
                            <div>
                                <div style={{ fontWeight: 700, color: "#1b2d55", marginBottom: "8px" }}>
                                    What is your age?
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                    {[
                                        ["18-24 years", "18_24"],
                                        ["25-34 years", "25_34"],
                                        ["35+ years", "35_plus"],
                                    ].map(([label, value]) =>
                                        renderOption(label, value, ageGroup, setAgeGroup)
                                    )}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontWeight: 700, color: "#1b2d55", marginBottom: "8px" }}>
                                    Preferred car type
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                    {[
                                        ["Manual", "manual"],
                                        ["Automatic", "automatic"],
                                    ].map(([label, value]) =>
                                        renderOption(label, value, carType, setCarType)
                                    )}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontWeight: 700, color: "#1b2d55", marginBottom: "8px" }}>
                                    Your gender
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                    {[
                                        ["Male", "male"],
                                        ["Female", "female"],
                                    ].map(([label, value]) =>
                                        renderOption(label, value, learnerGender, setLearnerGender)
                                    )}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontWeight: 700, color: "#1b2d55", marginBottom: "8px" }}>
                                    Preferred time
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                    {[
                                        ["Morning", "morning"],
                                        ["Afternoon", "afternoon"],
                                        ["Evening", "evening"],
                                        ["Weekend", "weekend"],
                                        ["Flexible", "flexible"],
                                    ].map(([label, value]) =>
                                        renderOption(label, value, preferredTime, setPreferredTime)
                                    )}
                                </div>
                            </div>

                            <div>
                                <div style={{ fontWeight: 700, color: "#1b2d55", marginBottom: "8px" }}>
                                    Preferred driver
                                </div>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                                    {[
                                        ["Male", "male"],
                                        ["Female", "female"],
                                    ].map(([label, value]) =>
                                        renderOption(label, value, driverPreference, setDriverPreference)
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {!allAnswered ? (
                        <div
                            style={{
                                marginTop: "16px",
                                borderRadius: "16px",
                                border: "1px dashed #c9d4f3",
                                background: "#f8faff",
                                padding: "16px",
                                color: "#627191",
                                lineHeight: 1.5,
                            }}
                        >
                            We will recommend the best-fit courses here after you complete
                            the five preference questions.
                        </div>
                    ) : !showResults ? (
                        <div
                            style={{
                                marginTop: "18px",
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Button
                                variant="primary"
                                className="rounded-pill px-5"
                                style={{ minWidth: "170px", minHeight: "48px", fontWeight: 700 }}
                                onClick={handleConfirm}
                            >
                                OK
                            </Button>
                        </div>
                    ) : recommendedCourses.length === 0 ? (
                        <div
                            style={{
                                marginTop: "16px",
                                borderRadius: "16px",
                                border: "1px dashed #c9d4f3",
                                background: "#f8faff",
                                padding: "16px",
                                color: "#627191",
                                lineHeight: 1.5,
                            }}
                        >
                            No course recommendations are available right now.
                        </div>
                    ) : (
                        <div style={{ display: "grid", gap: "10px", marginTop: "16px" }}>
                            <div
                                style={{
                                    fontWeight: 800,
                                    color: "#15264d",
                                    fontSize: "1.15rem",
                                }}
                            >
                                Recommended Courses
                            </div>
                            {recommendedCourses.map((course) => (
                                <div
                                    key={course.id}
                                    style={{
                                        borderRadius: "16px",
                                        border: "1px solid #e4e9f8",
                                        padding: "14px",
                                        background: "linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                                gap: "10px",
                                                marginBottom: "6px",
                                            }}
                                        >
                                        <h4
                                            style={{
                                                margin: 0,
                                                color: "#172a54",
                                                    fontSize: "0.98rem",
                                                    fontWeight: 700,
                                                }}
                                            >
                                            {course.title}
                                        </h4>
                                        <span
                                            style={{
                                                borderRadius: "999px",
                                                background: "#eef3ff",
                                                color: "#284eb8",
                                                    padding: "5px 9px",
                                                    fontSize: "0.76rem",
                                                    fontWeight: 700,
                                                    whiteSpace: "nowrap",
                                                }}
                                        >
                                            {course.duration} Day
                                        </span>
                                    </div>
                                    <p
                                        style={{
                                            marginBottom: "10px",
                                            color: "#627191",
                                            lineHeight: 1.4,
                                            fontSize: "0.88rem",
                                        }}
                                    >
                                        {(course.description || "Structured practical and theory-based driving training.")
                                            .slice(0, 140)}
                                        {(course.description || "").length > 140 ? "..." : ""}
                                    </p>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "8px",
                                        }}
                                    >
                                        <Button
                                            variant="primary"
                                            className="rounded-pill px-4"
                                            onClick={() => {
                                                if (typeof onSelectCourse === "function") {
                                                    onSelectCourse(course.id);
                                                } else if (course.slug && typeof onViewCourse === "function") {
                                                    onViewCourse(course);
                                                }
                                                onClose?.();
                                            }}
                                        >
                                            Enroll Now
                                        </Button>
                                        {course.slug ? (
                                            <Button
                                                variant="outline-primary"
                                                className="rounded-pill px-4"
                                                onClick={() => onViewCourse?.(course)}
                                            >
                                                View Details
                                            </Button>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                ) : null}

                <style>
                    {`
                        .course-recommendation-loader {
                            animation: courseRecommendationSpin 0.9s linear infinite;
                        }

                        @keyframes courseRecommendationSpin {
                            from { transform: rotate(0deg); }
                            to { transform: rotate(360deg); }
                        }

                    `}
                </style>
            </div>
        </div>
    );
};

export default CourseRecommendationModal;
